const keyManager = require("./keyManager");
const { encryptMessage, decryptMessage } = require("./encryption");
const api = require("../../api/secureMessageApi");

/**
 * Encrypts plaintext with the chat's session key and sends via REST.
 * Returns backend message payload (created message).
 */
async function sendMessage(chatId, plaintext) {
  console.log("[DEBUG] sendMessage called with:", { chatId, plaintext });

  if (!chatId) throw new Error("sendMessage: chatId is required");
  if (plaintext === undefined || plaintext === null)
    throw new Error("sendMessage: plaintext is required");

  console.log("[DEBUG] Input validation passed");

  // Step 1: Get session key
  console.log("[DEBUG] Step 1: Getting session key for chatId:", chatId);
  let keyBase64;
  try {
    keyBase64 = await keyManager.getSessionKey(chatId);
    console.log(
      "[DEBUG] Session key retrieved successfully:",
      keyBase64 ? "Key exists" : "No key"
    );
    console.log("[DEBUG] Key length:", keyBase64 ? keyBase64.length : 0);
  } catch (keyError) {
    console.error("[DEBUG] Failed to get session key:", keyError);
    throw new Error(`Session key retrieval failed: ${keyError.message}`);
  }

  // Step 2: Encrypt message
  console.log("[DEBUG] Step 2: Encrypting message");
  let ciphertext, iv, authTag;
  try {
    const encrypted = await encryptMessage(String(plaintext), keyBase64);
    ciphertext = encrypted.ciphertext;
    iv = encrypted.iv;
    authTag = encrypted.authTag;
    console.log("[DEBUG] Message encrypted successfully");
    console.log("[DEBUG] Ciphertext length:", ciphertext.length);
    console.log("[DEBUG] IV length:", iv.length);
    console.log("[DEBUG] AuthTag length:", authTag.length);
  } catch (encryptError) {
    console.error("[DEBUG] Encryption failed:", encryptError);
    throw new Error(`Encryption failed: ${encryptError.message}`);
  }

  // Step 3: Send to API
  console.log("[DEBUG] Step 3: Sending to API");
  try {
    const res = await api.sendMessage({ chatId, ciphertext, iv, authTag });
    console.log("[DEBUG] API call successful");
    console.log("[DEBUG] API response:", res);
    return res?.data || res;
  } catch (apiError) {
    console.error("[DEBUG] API call failed:", apiError);
    console.error("[DEBUG] API error details:", {
      message: apiError.message,
      status: apiError.status,
      response: apiError.response,
    });
    throw new Error(`API call failed: ${apiError.message}`);
  }
}

/**
 * Decrypt a single message object having { content, iv, authTag } fields.
 * Returns the same object with an added field: decryptedContent.
 */
async function decryptMessageObject(message, chatId) {
  if (!message) return null;
  const keyBase64 = await keyManager.getSessionKey(chatId || message.chat);
  const decryptedContent = await decryptMessage(
    message.content,
    keyBase64,
    message.iv,
    message.authTag
  );
  return { ...message, decryptedContent };
}

/**
 * Decrypt an array of message objects; resolves to array with decryptedContent.
 */
async function decryptMessages(messages, chatId) {
  if (!Array.isArray(messages)) return [];
  const keyBase64 = await keyManager.getSessionKey(
    chatId || (messages[0] && messages[0].chat)
  );
  const results = [];
  for (const m of messages) {
    try {
      const decryptedContent = await decryptMessage(
        m.content,
        keyBase64,
        m.iv,
        m.authTag
      );
      results.push({ ...m, decryptedContent });
    } catch (e) {
      results.push({
        ...m,
        decryptedContent: "",
        decryptError: e?.message || String(e),
      });
    }
  }
  return results;
}

/**
 * Edit a message by encrypting new text and issuing REST update.
 */
async function editMessage(messageId, chatId, newText) {
  if (!messageId) throw new Error("editMessage: messageId is required");
  if (!chatId) throw new Error("editMessage: chatId is required");
  const keyBase64 = await keyManager.getSessionKey(chatId);
  const { ciphertext, iv, authTag } = await encryptMessage(
    String(newText),
    keyBase64
  );
  const res = await api.editMessage(messageId, { ciphertext, iv, authTag });
  return res?.data || res;
}

/**
 * Soft-delete a message.
 */
async function deleteMessage(messageId) {
  if (!messageId) throw new Error("deleteMessage: messageId is required");
  const res = await api.deleteMessage(messageId);
  return res?.data || res;
}

/**
 * Mark messages as read in the chat.
 */
async function markMessagesRead(chatId, messageIds = []) {
  if (!chatId) throw new Error("markMessagesRead: chatId is required");
  const res = await api.markMessagesRead(chatId, messageIds);
  return res?.data || res;
}

module.exports = {
  sendMessage,
  decryptMessageObject,
  decryptMessages,
  editMessage,
  deleteMessage,
  markMessagesRead,
};
